import PageShell from '@/components/feature/PageShell';
import PageIntro from '@/components/feature/PageIntro';
import FinalCta from '@/components/feature/FinalCta';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import ImageReveal from '@/components/base/ImageReveal';
import TitleReveal from '@/components/base/TitleReveal';
import InkSettle from '@/components/base/InkSettle';
import ExposureResolve from '@/components/base/ExposureResolve';
import { advantages } from '@/mocks/services';

export default function AboutPage() {
  return (
    <PageShell>
      <PageIntro eyebrow="About MENDWELL" title="Repair, Done Quietly Well.">
        MENDWELL is a local in-home appliance repair company built around a simple idea: careful diagnostics, honest explanations and respect for your home.
      </PageIntro>
      <section className="bg-background-50 pb-16 md:pb-24">
        <Container>
          <ImageReveal className="h-[300px] sm:h-[440px] lg:h-[600px] w-full overflow-hidden rounded-xl">
            <img
              src="https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-about-hands.webp"
              width={1920}
              height={1000}
              alt="Technician carefully servicing a dishwasher"
              title="About MENDWELL appliance repair"
              className="h-full w-full object-cover object-top"
            />
          </ImageReveal>
        </Container>
      </section>
      <section className="bg-background-50 pb-20 md:pb-32">
        <Container>
          <div className="grid grid-cols-1 gap-10 border-t border-background-200 pt-12 md:pt-16 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Eyebrow label="Our Philosophy" />
              <InkSettle className="mt-6 text-[30px] md:text-[44px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">A repair should feel calm, not stressful.</InkSettle>
            </div>
            <div className="lg:col-span-7 space-y-5 text-[15px] md:text-base leading-relaxed text-foreground-600">
              <p>When an appliance stops working, your routine stops with it. We believe the visit that follows should make things simpler: a clear diagnosis, a plain explanation and a repair carried out with care.</p>
              <p>Our technicians take time to find the real cause before recommending anything. You hear what we found, what the repair involves and what it will cost before any work begins, so every decision stays yours.</p>
              <p>Inside your home, we work tidily and respectfully, protecting floors and leaving the space as we found it.</p>
            </div>
          </div>
          <div className="mt-14 md:mt-20 grid grid-cols-1 gap-6 md:grid-cols-2">
            <ExposureResolve className="h-[300px] md:h-[440px] w-full overflow-hidden rounded-xl bg-primary-50">
              <img src="https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-about-explain.webp" width={1000} height={880} loading="lazy" decoding="async" alt="Technician explaining a repair to a homeowner" title="Clear communication - MENDWELL appliance repair" className="h-full w-full object-cover object-top" />
            </ExposureResolve>
            <ExposureResolve className="h-[300px] md:h-[440px] w-full overflow-hidden rounded-xl bg-primary-50">
              <img src="https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-about-tools.webp" width={1000} height={880} loading="lazy" decoding="async" alt="Repair tools laid out on a protective mat" title="Careful workmanship - MENDWELL appliance repair" className="h-full w-full object-cover object-top" />
            </ExposureResolve>
          </div>
        </Container>
      </section>
      <section className="bg-background-100 py-20 md:py-32">
        <Container>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-16">
            <Eyebrow label="How We Work" />
            <TitleReveal className="text-[30px] md:text-[44px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">Three principles behind every visit.</TitleReveal>
          </div>
          <div className="mt-12">
            {advantages.map((a, i) => (
              <div key={a.title} className="grid grid-cols-1 gap-3 border-t border-background-200 py-8 lg:grid-cols-2 lg:gap-16 last:border-b">
                <span className="text-5xl md:text-7xl font-light leading-none text-primary-400">{`0${i + 1}`}</span>
                <div>
                  <h3 className="text-lg font-medium text-foreground-950">{a.title}</h3>
                  <p className="mt-2 max-w-md text-[15px] leading-relaxed text-foreground-600">{a.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>
      <FinalCta />
    </PageShell>
  );
}